import { useEffect, useRef } from "react";

/**
 * Fond animé de l'en-tête de la page Cours : des notions et formules du glossaire
 * qui montent doucement puis s'effacent.
 * - tous les mots montent à la même vitesse : aucun ne peut en rattraper un autre
 * - chaque nouveau mot est placé là où il ne chevauche aucun mot visible, et jamais en double
 * - occupe la partie droite et s'estompe vers la gauche (le texte reste lisible)
 * - image fixe si l'utilisateur a demandé moins d'animations (prefers-reduced-motion)
 * Le parent doit être en `position: relative` (et idéalement `overflow-hidden`).
 */
const NOTIONS = [
  "Intérêts composés", "(1 + r)ⁿ", "Diversification", "σ", "β", "PER", "DCA", "Drawdown",
  "Liquidité", "ETF", "Spread", "RSI", "Inflation", "Dividende", "Obligation", "VAN",
  "Effet de levier", "Corrélation", "Rendement", "Volatilité", "Capitalisation", "Krach",
];
const ORANGE = "247,152,55";
const WHITE = "255,255,255";
const SPEED = 0.022; // fraction de la hauteur par seconde (identique pour tous les mots)
const GAP = 18; // espace minimum entre deux mots (px)
const MAX_ITEMS = 14;
const FONT = "'IBM Plex Mono', monospace";

export default function NotionsBackground({ className = "" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = 0;
    let height = 0;
    let items = [];
    let nextSpawn = 0;
    let last = 0;
    let frame = 0;

    const box = (it) => {
      ctx.font = `${it.size}px ${FONT}`;
      const w = ctx.measureText(it.text).width;
      return {
        x0: width * it.x - GAP,
        x1: width * it.x + w + GAP,
        y0: height * it.y - it.size - GAP / 2,
        y1: height * it.y + GAP / 2,
      };
    };
    const isFree = (it) => {
      const a = box(it);
      if (a.x1 - GAP > width * 0.985) return false;
      return items.every((o) => {
        const b = box(o);
        return a.x1 < b.x0 || b.x1 < a.x0 || a.y1 < b.y0 || b.y1 < a.y0;
      });
    };
    const make = (y) => {
      const used = new Set(items.map((i) => i.text));
      const pool = NOTIONS.filter((n) => !used.has(n));
      return {
        text: pool[Math.floor(Math.random() * pool.length)],
        x: 0.42 + Math.random() * 0.5,
        y,
        size: 11 + Math.floor(Math.random() * 5),
        color: Math.random() < 0.35 ? ORANGE : WHITE,
        alpha: 0.15 + Math.random() * 0.2,
      };
    };
    // Essaie plusieurs emplacements, garde le premier qui ne chevauche rien
    const spawn = (y) => {
      for (let k = 0; k < 30; k += 1) {
        const it = make(y);
        if (isFree(it)) return it;
      }
      return null;
    };

    const setup = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      width = rect.width;
      height = rect.height;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      items = [];
      for (let k = 0; k < 60 && items.length < MAX_ITEMS; k += 1) {
        const it = spawn(0.05 + Math.random() * 1.05);
        if (it) items.push(it);
      }
      nextSpawn = 0;
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      items.forEach((it) => {
        const fade = Math.max(0, Math.min(1, (1.12 - it.y) * 3, (it.y + 0.1) * 3));
        ctx.font = `${it.size}px ${FONT}`;
        ctx.fillStyle = `rgba(${it.color},${it.alpha * fade})`;
        ctx.fillText(it.text, width * it.x, height * it.y);
      });
    };

    const tick = (now) => {
      const dt = Math.min(0.1, (now - (last || now)) / 1000);
      last = now;
      items.forEach((it) => {
        it.y -= SPEED * dt;
      });
      items = items.filter((it) => it.y > -0.1);
      nextSpawn -= dt;
      if (nextSpawn <= 0 && items.length < MAX_ITEMS) {
        const it = spawn(1.12);
        if (it) items.push(it);
        nextSpawn = 0.6 + Math.random() * 0.8;
      }
      draw();
      frame = requestAnimationFrame(tick);
    };

    setup();
    draw();
    if (!reduceMotion) frame = requestAnimationFrame(tick);

    const observer = new ResizeObserver(() => {
      setup();
      draw();
    });
    observer.observe(canvas);

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, []);

  const mask = "linear-gradient(90deg, transparent 0%, transparent 40%, #000 75%)";
  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`hero-anim absolute inset-0 w-full h-full pointer-events-none ${className}`}
      style={{ WebkitMaskImage: mask, maskImage: mask }}
      data-testid="courses-notions-bg"
    />
  );
}
